import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { Button, Grid, Typography, useTheme } from "@mui/material";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { toast } from "react-toastify";
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { reorder } from "@atlaskit/pragmatic-drag-and-drop/reorder";
import { triggerPostMoveFlash } from "@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash";
import { extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { getReorderDestinationIndex } from "@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index";
import * as liveRegion from "@atlaskit/pragmatic-drag-and-drop-live-region";
import invariant from "tiny-invariant";
import { getItemRegistry, isItemData } from "../../../utils/dndUtils";
import ThirdPlaceListItem from "./ThirdPlaceListItem";

const ListContext = createContext(null);

export const useListContext = () => {
  const listContext = useContext(ListContext);
  invariant(listContext !== null);
  return listContext;
};

const ThirdPlaceList = (props) => {
  const { thirdPlaceTeams, updateThirdPlacePicks, commitThirdPlace } = props;
  const theme = useTheme();

  const picksProgress = useSelector((state) => state.app?.picksProgress);

  const [{ items, lastCardMoved }, setListState] = useState({
    items: thirdPlaceTeams,
    lastCardMoved: null,
  });
  const [registry] = useState(getItemRegistry);
  const [accepting, setAccepting] = useState(false);

  // Accept the default top-8 order without a drag. No-op once registered.
  const handleAcceptThirdPlace = useCallback(async () => {
    if (!commitThirdPlace || picksProgress?.thirdPlaceComplete || accepting)
      return;
    setAccepting(true);
    const ok = await commitThirdPlace();
    setAccepting(false);
    if (!ok) {
      toast("Couldn't save the 3rd place race — please try again", {
        type: "error",
        position: "bottom-right",
        autoClose: 2500,
        hideProgressBar: true,
        closeOnClick: false,
      });
    }
  }, [commitThirdPlace, picksProgress?.thirdPlaceComplete, accepting]);

  // Isolated instances of this component from one another
  const [instanceId] = useState(() => Symbol("instance-id"));

  const reorderItem = useCallback(
    ({ startIndex, indexOfTarget, closestEdgeOfTarget }) => {
      const finishIndex = getReorderDestinationIndex({
        startIndex,
        closestEdgeOfTarget,
        indexOfTarget,
        axis: "vertical",
      });

      if (finishIndex === startIndex) {
        // If there would be no change, we skip the update
        return;
      }

      setListState((listState) => {
        const item = listState.items[startIndex];

        return {
          items: reorder({
            list: listState.items,
            startIndex,
            finishIndex,
          }),
          lastCardMoved: {
            item,
            previousIndex: startIndex,
            currentIndex: finishIndex,
            numberOfItems: listState.items.length,
          },
        };
      });
    },
    [],
  );

  useEffect(() => {
    if (
      !!thirdPlaceTeams &&
      !thirdPlaceTeams.every((val, index) => val === items[index])
    ) {
      setListState({
        lastCardMoved,
        items: thirdPlaceTeams,
      });
    }
  }, [thirdPlaceTeams, lastCardMoved, items]);

  useEffect(() => {
    return monitorForElements({
      canMonitor({ source }) {
        return isItemData(source.data) && source.data.instanceId === instanceId;
      },
      onDrop({ location, source }) {
        const target = location.current.dropTargets[0];
        if (!target) {
          return;
        }

        const sourceData = source.data;
        const targetData = target.data;
        if (!isItemData(sourceData) || !isItemData(targetData)) {
          return;
        }

        const indexOfTarget = items.findIndex(
          (item) => item.id === targetData.item.id,
        );
        if (indexOfTarget < 0) {
          return;
        }

        const closestEdgeOfTarget = extractClosestEdge(targetData);

        reorderItem({
          startIndex: sourceData.index,
          indexOfTarget,
          closestEdgeOfTarget,
        });
      },
    });
  }, [instanceId, items, reorderItem]);

  // once a drag is finished, some post drop actions
  useEffect(() => {
    if (lastCardMoved === null) {
      return;
    }

    const { item, previousIndex, currentIndex, numberOfItems } = lastCardMoved;
    const element = registry.getElement(item.id);
    if (element) {
      triggerPostMoveFlash(element);
    }

    liveRegion.announce(
      `You've moved ${item.label} from position ${
        previousIndex + 1
      } to position ${currentIndex + 1} of ${numberOfItems}.`,
    );
  }, [lastCardMoved, registry]);

  // cleanup the live region when this component is finished
  useEffect(() => {
    return function cleanup() {
      liveRegion.cleanup();
    };
  }, []);

  const getListLength = useCallback(() => items?.length, [items?.length]);

  const contextValue = useMemo(() => {
    return {
      registerItem: registry.register,
      reorderItem,
      instanceId,
      getListLength,
    };
  }, [registry.register, reorderItem, instanceId, getListLength]);

  return (
    <Grid
      key={"third-place-picks"}
      id={"third-place-picks"}
      container
      direction="column"
      sx={[
        {
          width: "100%",
          height: "fit-content",
          position: "relative",
          borderRadius: "14px",
          border: `1px solid ${theme.branding.gray[200]}`,
          backgroundColor: theme.branding.gray[0],
          boxShadow: `-2px 10px 20px -3px ${theme.branding.gray[200]}, 0 4px 6px -4px ${theme.branding.gray[300]}`,
        },
        (theme) =>
          theme.applyStyles("dark", {
            borderColor: theme.branding.darkBlue[600],
            backgroundColor: theme.branding.darkBlue[800],
          }),
      ]}
    >
      <Grid
        container
        direction="column"
        sx={{ gap: "8px", padding: "16px 24px" }}
      >
        <Grid
          container
          direction="row"
          wrap="nowrap"
          sx={{ gap: "12px", alignItems: "center" }}
        >
          <EmojiEventsOutlinedIcon
            sx={{
              width: "20px",
              height: "20px",
              color: theme.branding.yellow[500],
            }}
          />
          <Typography variant="h5">🥉 3rd Place Race</Typography>
          {/* registration indicator — mirrors the group pills' complete check */}
          <Grid
            container
            wrap="nowrap"
            sx={{
              width: "auto",
              marginLeft: "auto",
              alignItems: "center",
              gap: "4px",
              padding: "2px 10px",
              borderRadius: "999px",
              ...(picksProgress?.thirdPlaceComplete
                ? { backgroundColor: theme.branding.cyan[500] }
                : {
                    backgroundColor: "transparent",
                    border: `1px solid ${theme.branding.gray[200]}`,
                  }),
            }}
          >
            {picksProgress?.thirdPlaceComplete && (
              <CheckOutlinedIcon
                sx={{
                  width: "14px",
                  height: "14px",
                  color: theme.branding.gray[0],
                }}
              />
            )}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: picksProgress?.thirdPlaceComplete
                  ? theme.branding.gray[0]
                  : theme.branding.gray[500],
              }}
            >
              {picksProgress?.thirdPlaceComplete
                ? "Registered"
                : "Not registered"}
            </Typography>
          </Grid>
        </Grid>
        <Typography variant="caption">Top 8 advance</Typography>
      </Grid>

      {/* list items */}
      <ListContext.Provider value={contextValue}>
        <Grid
          container
          direction="column"
          sx={[
            {
              gap: "8px",
              padding: "16px 24px",
              borderTop: `1px solid ${theme.branding.gray[200]}`,
            },
            (theme) =>
              theme.applyStyles({
                borderColor: theme.branding.darkBlue[600],
              }),
          ]}
        >
          {/* if all groups are NOT complete, render some dummy items */}
          {picksProgress?.allGroupsComplete
            ? items?.map((item, index) => (
                <Grid
                  key={`tp-li-container-${item?.id}-${index}`}
                  container
                  sx={{
                    opacity: picksProgress?.thirdPlaceComplete ? "100%" : "60%",
                    width: "100%",
                  }}
                >
                  <ThirdPlaceListItem
                    key={`tp-li-${item?.id}-${index}`}
                    item={item}
                    index={index}
                    thirdPlaceTeams={items}
                    updateThirdPlacePicks={updateThirdPlacePicks}
                  />
                </Grid>
              ))
            : new Array(8).fill("").map((dummyItem, index) => (
                <Grid
                  container
                  direction="row"
                  wrap="nowrap"
                  sx={[
                    {
                      width: "100%",
                      padding: "12px",
                      gap: "8px",
                      border: `2px dashed ${theme.branding.gray[200]}`,
                      borderRadius: "10px",
                    },
                    (theme) =>
                      theme.applyStyles("dark", {
                        borderColor: theme.branding.darkBlue[600],
                      }),
                  ]}
                >
                  <Grid
                    container
                    sx={[
                      {
                        height: "24px",
                        maxWidth: "24px",
                        width: "100%",
                        borderRadius: "100%",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: theme.branding.gray[100],
                      },
                      (theme) =>
                        theme.applyStyles("dark", {
                          backgroundColor: theme.branding.darkBlue[600],
                        }),
                    ]}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: theme.branding.gray[300] }}
                    >
                      {/* number the dummies starting from the last number of the real items */}
                      {index + 1}
                    </Typography>
                  </Grid>
                  <Grid
                    container
                    direction="column"
                    sx={{ gap: "4px", width: "100%" }}
                  >
                    <Grid
                      sx={[
                        {
                          borderRadius: "4px",
                          maxWidth: "96px",
                          width: "100%",
                          height: "12px",
                          backgroundColor: theme.branding.gray[100],
                        },
                        (theme) =>
                          theme.applyStyles("dark", {
                            backgroundColor: theme.branding.darkBlue[600],
                          }),
                      ]}
                    />
                    <Grid
                      sx={[
                        {
                          borderRadius: "2px",
                          maxWidth: "64px",
                          width: "100%",
                          height: "8px",
                          backgroundColor: theme.branding.gray[100],
                        },
                        (theme) =>
                          theme.applyStyles("dark", {
                            backgroundColor: theme.branding.darkBlue[600],
                          }),
                      ]}
                    />
                  </Grid>
                </Grid>
              ))}
        </Grid>
      </ListContext.Provider>

      {/* accept the default top-8 order without a drag (only once every group
          is registered and the race is populated) */}
      {picksProgress?.allGroupsComplete &&
        !picksProgress?.thirdPlaceComplete &&
        items?.length > 0 && (
          <Grid container sx={{ padding: "0px 24px 16px" }}>
            <Button
              fullWidth
              variant="outlined"
              disabled={accepting}
              onClick={handleAcceptThirdPlace}
              sx={{
                borderRadius: "10px",
                padding: "8px 16px",
                borderColor: theme.branding.teal,
                color: theme.branding.teal,
              }}
            >
              <Typography
                variant="button"
                sx={{ fontWeight: 600, color: "inherit" }}
              >
                {accepting ? "Saving…" : "Accept top 8"}
              </Typography>
            </Button>
          </Grid>
        )}

      {/* footer */}
      {items?.length === 0 && (
        <Grid
          container
          sx={{ padding: "0px 16px 12px", justifyContent: "center" }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.branding.gray[400],
              fontStyle: "italic",
              textAlign: "center",
            }}
          >
            Pick 3rd place teams in each group to see who advances
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default ThirdPlaceList;
